import { justDate, addDays, isWeekend } from './date-utils';
import { isHoliday } from './holiday-calculator';

const CREWS = 2;

// ── Monday-based week start (matches iOS) ─────────────────────────────────────
function startOfWeekMonday(date) {
  const d = justDate(date);
  const day = d.getDay(); // 0 = Sunday, 1 = Monday ... 6 = Saturday
  const diff = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  return d;
}

// Check if date is a workable day (not weekend, not holiday)
function isWorkableDay(date) {
  if (isWeekend(date)) return false;
  if (isHoliday(date)) return false;
  return true;
}

// Get next workday on or after given date
function nextWorkday(date) {
  let d = justDate(date);
  while (!isWorkableDay(d)) {
    d = addDays(d, 1);
  }
  return d;
}

// Weekly job limit based on month (winter constraint)
function weeklyJobLimit(date) {
  const month = new Date(date).getMonth() + 1;
  // Winter (Dec, Jan, Feb): max 3 jobs/week
  return (month === 12 || month === 1 || month === 2) ? 3 : 6;
}

// Crew-days required for a job
function crewDaysRequired(job) {
  const duration = Math.max(job.durationDays || 1, 1);
  // Jobs > 2 days consume both crews
  if (duration > 2) {
    return duration * CREWS;
  }
  // 1-2 day jobs use one crew
  return duration;
}

// Count workable days in a week starting from weekStart (Monday)
function workdaysInWeek(weekStart) {
  let count = 0;
  for (let i = 0; i < 7; i++) {
    const day = addDays(weekStart, i);
    if (isWorkableDay(day)) count++;
  }
  return count;
}

// Crew-days used by scheduled jobs in a week (Monday-based)
function usedCrewDaysInWeek(weekStart, allJobs) {
  const start = startOfWeekMonday(weekStart);
  const end = addDays(start, 7);

  let total = 0;
  allJobs.forEach(job => {
    const scheduledDate = job.scheduledDate ? justDate(job.scheduledDate) : null;
    if (scheduledDate && scheduledDate >= start && scheduledDate < end) {
      total += crewDaysRequired(job);
    }
  });

  return total;
}

// Count scheduled jobs in a week (Monday-based)
function scheduledJobsInWeek(weekStart, allJobs) {
  const start = startOfWeekMonday(weekStart);
  const end = addDays(start, 7);

  return allJobs.filter(job => {
    const scheduledDate = job.scheduledDate ? justDate(job.scheduledDate) : null;
    return scheduledDate && scheduledDate >= start && scheduledDate < end;
  }).length;
}

// Main sold-out estimation algorithm (matches iOS JobService)
export function estimateSoldOutDate(allJobs) {
  // Backlog: unscheduled, not repair, not completed/cancelled
  let backlog = allJobs.filter(job => {
    const hasScheduledDate = !!job.scheduledDate;
    const isRepair = job.isRepair || false;
    const status = (job.status || '').toLowerCase();
    const isClosed = status === 'completed' || status === 'cancelled';

    return !hasScheduledDate && !isRepair && !isClosed;
  });

  if (backlog.length === 0) {
    return null; // No backlog
  }

  // Sort by sold date (oldest first)
  backlog.sort((a, b) => {
    const dateA = a.soldDate ? new Date(a.soldDate) : new Date(0);
    const dateB = b.soldDate ? new Date(b.soldDate) : new Date(0);
    return dateA - dateB;
  });

  let pointer = nextWorkday(justDate(new Date()));

  while (backlog.length > 0) {
    // Use Monday-based week start to match iOS
    const weekStart = startOfWeekMonday(pointer);
    const jobLimit = weeklyJobLimit(weekStart);

    // Jobs already scheduled in this week
    const alreadyScheduledCount = scheduledJobsInWeek(weekStart, allJobs);
    let jobsRemainingThisWeek = Math.max(jobLimit - alreadyScheduledCount, 0);

    // Crew capacity for this week
    const workableDays = workdaysInWeek(weekStart);
    let crewDaysRemaining = CREWS * workableDays;
    crewDaysRemaining -= usedCrewDaysInWeek(weekStart, allJobs);
    crewDaysRemaining = Math.max(crewDaysRemaining, 0);

    // Try to assign backlog jobs to this week
    const assignedIndexes = [];
    backlog.forEach((job, index) => {
      const cost = crewDaysRequired(job);
      if (jobsRemainingThisWeek > 0 && crewDaysRemaining >= cost) {
        jobsRemainingThisWeek -= 1;
        crewDaysRemaining -= cost;
        assignedIndexes.push(index);
      }
    });

    // Remove assigned jobs from backlog
    assignedIndexes.sort((a, b) => b - a).forEach(i => {
      backlog.splice(i, 1);
    });

    if (backlog.length === 0) {
      // All jobs assigned — sold out through this week, no buffer
      return nextWorkday(weekStart);
    }

    // Move to next week
    pointer = addDays(weekStart, 7);
  }

  return null;
}